import {
  forwardRef,
  RadioGroup as ChakraRadioGroup,
  type RadioGroupProps as ChakraRadioGroupProps,
} from '@chakra-ui/react-v2';
import type { Merge } from 'type-fest';

import { Stack, type StackProps } from '@frontend/legacy-components';

import { DEFAULT_PROPS } from './const';
import type { RadioSize } from './types';

const SIZE_TO_VERTICAL_SPACING_MAP = {
  lg: '0.75rem',
  md: '0.5rem',
  sm: '0.25rem',
} as const satisfies Record<RadioSize, string>;

const SIZE_TO_HORIZONTAL_SPACING_MAP = {
  lg: '1rem',
  md: '1rem',
  sm: '0.75rem',
} as const satisfies Record<RadioSize, string>;

export type RadioGroupProps = Merge<
  StackProps,
  Merge<ChakraRadioGroupProps, { size?: RadioSize }>
>;

export const RadioGroup = forwardRef<RadioGroupProps, 'div'>(
  (
    {
      size = DEFAULT_PROPS.size,
      direction = DEFAULT_PROPS.direction,
      spacing: spacingProp,
      children,
      ...restProps
    }: RadioGroupProps,
    ref,
  ) => {
    return (
      <ChakraRadioGroup
        ref={ref}
        size={size}
        as={Stack}
        direction={direction}
        width="fit-content"
        maxWidth="100%"
        // TODO: PP-17160: Fix spacing to use DEFAULT_PROPS.size so that size-
        // specific spacing is applied for implicitly sized RadioGroups (i.e.
        // when size is undefined)
        spacing={
          spacingProp || direction === 'row'
            ? SIZE_TO_HORIZONTAL_SPACING_MAP[size]
            : SIZE_TO_VERTICAL_SPACING_MAP[size] // direction == 'column', and default
        }
        {...restProps}
      >
        {children}
      </ChakraRadioGroup>
    );
  },
);
RadioGroup.displayName = 'RadioGroup';
